// utils/API/BackendHelper/admin/analytics.js
import ApiHelper from '../../ApiHelper';
import API_ENDPOINTS from '../../endpoints';

class AdminAnalyticsHelper {
  /**
   * Get comprehensive system analytics
   * @returns {Promise} Complete analytics data
   */
  async getSystemAnalytics(forceRefresh = false) {
    try {
      // The backend caches analytics briefly; refresh=true bypasses that cache
      const response = await ApiHelper.get(
        API_ENDPOINTS.ADMIN.ANALYTICS.BASE,
        forceRefresh ? { refresh: 'true' } : {}
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch system analytics: ${error.message}`);
    }
  }

  /**
   * Get active users statistics
   * @returns {Promise} Active users data
   */
  async getActiveUsers() {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.ADMIN.ANALYTICS.ACTIVE_USERS);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch active users: ${error.message}`);
    }
  }

  /**
   * Get enrollment statistics
   * @returns {Promise} Enrollment data
   */
  async getEnrollmentStats() {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.ADMIN.ANALYTICS.ENROLLMENTS);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch enrollment statistics: ${error.message}`);
    }
  }

  /**
   * Get monthly growth data
   * @param {number} months - Number of months to fetch (default: 6)
   * @returns {Promise} Monthly growth data
   */
  async getMonthlyGrowth(months = 6) {
    try {
      const response = await ApiHelper.get(
        `${API_ENDPOINTS.ADMIN.ANALYTICS.MONTHLY_GROWTH}?months=${months}`
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch monthly growth: ${error.message}`);
    }
  }

  /**
   * Get top performing colleges
   * @param {number} limit - Number of colleges to return (default: 10)
   * @returns {Promise} Top colleges data
   */
  async getTopColleges(limit = 10) {
    try {
      const response = await ApiHelper.get(
        `${API_ENDPOINTS.ADMIN.ANALYTICS.TOP_COLLEGES}?limit=${limit}`
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch top colleges: ${error.message}`);
    }
  }

  /**
   * Get popular courses
   * @param {number} limit - Number of courses to return (default: 10)
   * @returns {Promise} Popular courses data
   */
  async getPopularCourses(limit = 10) {
    try {
      const response = await ApiHelper.get(
        `${API_ENDPOINTS.ADMIN.ANALYTICS.POPULAR_COURSES}?limit=${limit}`
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch popular courses: ${error.message}`);
    }
  }

  /**
   * Get subject-wise completion rates
   * @returns {Promise} Subject completion rates
   */
  async getSubjectCompletionRates() {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.ADMIN.ANALYTICS.SUBJECT_COMPLETION);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch subject completion rates: ${error.message}`);
    }
  }

  /**
   * Get exam statistics
   * @returns {Promise} Exam statistics data
   */
  async getExamStats() {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.ADMIN.ANALYTICS.EXAMS);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch exam statistics: ${error.message}`);
    }
  }

  /**
   * Get certificate statistics
   * @returns {Promise} Certificate statistics data
   */
  async getCertificateStats() {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.ADMIN.ANALYTICS.CERTIFICATES);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch certificate statistics: ${error.message}`);
    }
  }
}

export default new AdminAnalyticsHelper();